(ns client-portal.client.layout
  (:require ["react" :as react]
            [client-portal.client.anonymous.data :as anon.d]
            [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.concepts.views :as concepts]
            [client-portal.client.locations.views :as locations]
            [client-portal.client.products.google-cloud-projects.views :as google-cloud-projects]
            [client-portal.client.products.views]
            [client-portal.client.routes :as routes]
            [client-portal.client.core.data :as core.d]
            [client-portal.client.core.views :as core.views]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [client-portal.client.router :as router]
            [client-portal.client.system-configuration.data]
            [clojure.string :as s]
            [shared.core.account.data :as account.d]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.components.components :as c]
            [shared.core.config :as config]
            [shared.core.reactive :as re]
            [shared.core.toasts.views :as toasts]
            [shared.core.style :as style]
            [shared.core.util :as util]
            [shared.core.components.auth0.data]))

(def root-path [:layout])
(def header-height 60)
(def mobile-header-height 60)
(def base-nav-width 260)
(def mobile-nav-width 60)
(def layout-nav-id :layout-nav)
(def core-scroll-content-id "core-scroll-content")

;; Preserves the nav scroll position across route-change remounts of the sidebar.
(defonce sidebar-scroll-top* (atom 0))

(style/add-css [".vi-concept-dropdown>input"
                {:padding "3px 6px !important"}])

;; @up/extended-ui's PanelLayout only shows both panels from xl up, hiding the left one
;; below that while a right panel is open; keep both side by side down to md instead
;; (see `:layout/single-pane?-`).
(style/add-css "@media (max-width: 767px) { .core-panel-layout-373 > [data-slot=panel-left]:has(~ [data-slot=panel-right]) { display: none; } }")
(style/add-css "@media (min-width: 768px) { .core-panel-layout-373 { grid-template-columns: minmax(0, 1fr) min(360px, 50%) !important; } }")
(style/add-css "@media (min-width: 1280px) { .core-panel-layout-373 { grid-template-columns: minmax(0, 1fr) 448px !important; } }")

(style/add-css "@media (max-width: 767px) { input, select, textarea { font-size: 16px !important; } }")

(style/add-css "[data-slot=sidebar][data-mobile=true] { width: 100% !important; max-width: none !important; border: none !important; }")

(style/add-css ".core-panel-layout-373 > .panel-left-collapsed { display: none !important; }
                .core-panel-layout-373 > .panel-right-expanded { grid-column: 1 / -1; }")

(style/add-css
  [".markdown-body" {:background-color "transparent"}]
  [".markdown-body ol, .markdown-body ul, .markdown-body menu" {:list-style "disc"}]
  ["html.dark .markdown-body"
   {:color "rgba(255, 255, 255, 0.8)"}]
  [(str "html.dark .markdown-body h1,"
        "html.dark .markdown-body h2,"
        "html.dark .markdown-body h3,"
        "html.dark .markdown-body h4,"
        "html.dark .markdown-body h5,"
        "html.dark .markdown-body h6")
   {:color "rgba(255, 255, 255, 0.9)"
    :border-bottom-color "rgba(255, 255, 255, 0.15)"}]
  ["html.dark .markdown-body a"
   {:color "#58a6ff"}]
  ["html.dark .markdown-body strong"
   {:color "rgba(255, 255, 255, 0.95)"}]
  ["html.dark .markdown-body code"
   {:background-color "rgba(255, 255, 255, 0.1)"
    :color "rgba(255, 255, 255, 0.9)"}]
  ["html.dark .markdown-body pre"
   {:background-color "rgba(255, 255, 255, 0.05)"
    :color "rgba(255, 255, 255, 0.9)"}]
  ["html.dark .markdown-body pre code"
   {:background-color "transparent"
    :color "rgba(255, 255, 255, 0.9)"}]
  ["html.dark .markdown-body blockquote"
   {:color "rgba(255, 255, 255, 0.6)"
    :border-left-color "rgba(255, 255, 255, 0.3)"}]
  ["html.dark .markdown-body hr"
   {:background-color "rgba(255, 255, 255, 0.2)"
    :border-bottom-color "rgba(255, 255, 255, 0.2)"}]
  [(str "html.dark .markdown-body table th,"
        "html.dark .markdown-body table td")
   {:border-color "rgba(255, 255, 255, 0.2)"
    :color "rgba(255, 255, 255, 0.85)"}]
  ["html.dark .markdown-body table tr"
   {:background-color "transparent"
    :border-top-color "rgba(255, 255, 255, 0.2)"}]
  ["html.dark .markdown-body table tr:nth-child(2n)"
   {:background-color "rgba(255, 255, 255, 0.05)"}])

(defn active-toasts [_]
  (let [toasts- (re/subscribe [:toasts/active-])]
    (fn []
      (when (some-> @(re/subscribe [:toasts/active-]) (count) (pos?))
        (into [:div {:style {:display :flex
                             :flex-direction :column
                             :overflow :auto
                             :position :fixed
                             :bottom 20
                             :right 20
                             :max-height "calc(100% - 20px)"
                             :z-index (style/z :above-all)}}
               [alc/up-button {:on-click #(re/dispatch [:toasts/remove-all])
                               :style {:flex "0 0 auto"
                                       :margin-top 10}}
                "Dismiss All"]]
              (map (fn [t]
                     [toasts/toast (assoc t :alc? true)])
                   @toasts-))))))

(defn main [_]
  (let [theme- (re/subscribe [:core/theme-])
        full-render?* (re/ratom false)
        set-theme #(when-let [el (.querySelector js/document "html")]
                     (let [class-list (.-classList el)]
                       (if (= @theme- :dark-mode)
                         (.add class-list "dark")
                         (.remove class-list "dark")))
                     (reset! full-render?* true))
        vw- (re/subscribe [:core/viewport-width])]
    (re/create-class
      {:component-did-mount (fn [this]
                              (set-theme)
                              (let [{:keys [key-bindings]} (re/props this)]
                                (when key-bindings
                                  (re/dispatch [:keybind/add-current-route-bindings key-bindings]))))
       :component-did-update set-theme
       :reagent-render (fn [{:keys [container-style inner-container-style style]} & contents]
                         @theme-
                         (if @full-render?*
                           (let [mini? (< @vw- 1024)]
                             [c/theme
                              :div {:style (merge {:background-color (style/color :white :base)
                                                   :display :flex
                                                   :flex "1 0 0px"
                                                   :flex-direction :column}
                                                  container-style)
                                    :theme (style/dark-style {:background-color (style/dark-color :background :base)})}
                              [:div {:style (merge {:flex "1 1 auto"
                                                    :position :relative
                                                    :display :flex}
                                                   inner-container-style)}
                               (when-not mini?
                                 [alc/active-dialog])
                               (when-not mini?
                                 [toasts/active-toasts {:primary? true}])
                               (into [:div {:style (merge {:display :flex
                                                           :flex-direction :column
                                                           :flex "1 0 auto"
                                                           :max-width "100%"}
                                                          style)}]
                                     (if mini?
                                       [[:div {:style {:display :flex
                                                       :flex-direction :column
                                                       :align-items :center
                                                       :justify-content :center
                                                       :padding 20
                                                       :height "100%"
                                                       :width "100%"}}
                                         [:img {:src "/images/logo-dark.svg"
                                                :height 35
                                                :width :auto
                                                :style {:height 35}}]
                                         [:div {:style {:color (style/color :primary :base)
                                                        :text-align :center
                                                        :margin-top 20}}
                                          "The Client Portal is not yet optimized for smaller screen sizes"]]]
                                       contents))]])
                           [:div]))})))

(defn main-with-scroll [{:keys [scroll-panel-style content-container-style] :as args} & contents]
  [main args
   [:div {:style {:display :flex
                  :flex "1 1 auto"
                  :height "100%"}}
    [:div {:style (merge {:flex "1 1 auto"
                          :display :flex
                          :flex-direction :column
                          :overflow :auto
                          :height "100%"}
                         scroll-panel-style)}
     (into [:div {:style (merge {:flex "0 0 auto"
                                 :padding "40px 40px 60px"
                                 :min-height "100vh"
                                 :width "100%"}
                                content-container-style)}]
           contents)]]])

(defn message-section [msg]
  [c/resp :div {:style {:display :flex
                        :align-items :center
                        :justify-content :center
                        :padding "80px 30px"
                        :width "100%"}
                :resp {:lg {:style {:padding "60px 25px"}}}}
   [c/theme
    c/resp :span {:style {:color (style/color :on-background :base)
                          :font-size 20
                          :font-weight style/font-medium-weight
                          :line-height 1.3
                          :text-align :center
                          :opacity 0.5}
                  :theme (style/dark-style {:color (style/dark-color :on-background :base)})
                  :resp {:lg {:style {:font-size 18}}}}
    msg]])

(defn double-click-tooltip-display [{:keys [style]}]
  [:div {:style (merge {:color (style/alc-color "Text/secondary")}
                       style)}
   [c/material-icon {:icon-type :ads-click
                     :style {:display :inline-block
                             :color :inherit
                             :font-size 12}}]
   [:span {:style {:color :inherit
                   :margin-left 6}}
    "Double click to view details (hold"]
   [:div {:style {:display :inline-block}}
    [:div {:style {:background-color (style/alc-color "Background/card_hex")
                   :border-radius 3
                   :color :inherit
                   :font-size 11
                   :display :flex
                   :align-items :center
                   :justify-content :center
                   :margin "0 4px"
                   :padding "0 3px"}}
     (if (util/mac?) "⌘" "ctrl")]]
   [:span {:style {:color :inherit}}
    "to open in a new tab)"]])

(defn content-with-logo-in-corner [{:keys [main-with-scroll-args logo-on-click]} & contents]
  [main-with-scroll main-with-scroll-args
   [:div {:style (cond-> {:display :flex
                          :align-items :center
                          :padding "0 20px"
                          :position :absolute
                          :top 0
                          :left 0
                          :height header-height}
                         logo-on-click (assoc :cursor :pointer))
          :on-click logo-on-click}
    [:img {:src "/images/logo-dark.svg"
           :height 35
           :width :auto
           :style {:height 35}}]]
   (into [:div {:style {:flex "0 0 auto"
                        :display :flex
                        :flex-direction :column
                        :align-items :center
                        :justify-content :center
                        :padding "90px 30px 40px"
                        :height "100%"
                        :width "100%"}}]
         contents)])

(defn user-profile-picture [{:keys [picture profile-size style]
                             :or {profile-size 40}}]
  [:div {:style (merge {:background-color (style/color :gray :shade-7)
                        :background-image (str "url(" picture ")")
                        :background-position "50%, 50%"
                        :background-size :contain
                        :border-radius "50%"
                        :flex (str "0 0 " profile-size "px")
                        :height profile-size
                        :width profile-size}
                       style)}])

(defn account-dropdown [{:keys [profile-size style] :or {profile-size 40}}]
  (let [user- (re/subscribe [:auth0/user-])
        loading?- (re/subscribe [:auth0/loading?-])
        core-route-access- (re/subscribe [:account/core-route-access-])
        multi-core-route-access?- (re/subscribe [:account/multi-core-route-access?-])
        admin?- (re/subscribe [:account/admin-role?-])]
    (when (or @loading?- @user-)
      (if @loading?-
        [:div {:style (merge {:background-color (style/color :gray :shade-7)
                              :border-radius "50%"
                              :flex (str "0 0 " profile-size "px")
                              :height profile-size
                              :width profile-size}
                             style)}]
        [c/label-menu {:options (->> (concat (when @multi-core-route-access?-
                                               (->> @core-route-access-
                                                    (mapv #(let [route-config (get routes/routes %)]
                                                             {:text (:label route-config)
                                                              :on-click (fn [_]
                                                                          (if-let [r-dispatch (:dispatch route-config)]
                                                                            (re/dispatch r-dispatch)
                                                                            (re/dispatch [:router/go [%]])))}))
                                                    (filterv :text)))
                                             [(when @admin?-
                                                {:text [c/checkbox {:val-subscribe [:anonymous/enabled?-]
                                                                    :container-style {:pointer-events :none}
                                                                    :label "Anonymous Mode"}]
                                                 :on-click #(re/dispatch [:anonymous/toggle])})
                                              {:text "Log Out"
                                               :on-click #(re/dispatch [:auth0/logout])}])
                                     (filter identity))
                       :label [c/interactive
                               :span {:style {:display :flex
                                              :align-items :center
                                              :transition "0.2s"}
                                      :hover-style {:opacity 0.8}}
                               [user-profile-picture (merge @user- {:profile-size profile-size})]]
                       :placement :bottom-end
                       :tooltip-container-style {:width 175}}]))))

(defn account-dropdown-v2 [_]
  (let [user- (re/subscribe [:auth0/user-])
        admin?- (re/subscribe [:account/admin-role?-])
        manages-users?- (re/subscribe [:account/has-some-role?- [:user-manager]])
        anonymous?- (re/subscribe [:anonymous/enabled?-])]
    (fn [{:keys [profile-size] :or {profile-size 40}}]
      (let [{:keys [name email]} @user-
            set-theme (fn [to-theme]
                        (let [system? (:system to-theme)
                              to-dark-mode? (if system? (util/dark-mode-system-setting?) (= to-theme :dark-mode))]
                          (re/dispatch [:core/set-theme to-theme])
                          (let [class-list (.. js/document -documentElement -classList)]
                            (if to-dark-mode?
                              (.add class-list "dark")
                              (.remove class-list "dark")))))
            ;; One slot width for every item's icon or checkbox, so they share a center line and the labels line up
            menu-item-leading (fn [content]
                                [:span {:class-name "flex size-6 shrink-0 items-center justify-center"}
                                 content])]
        [alc/up-dropdown-menu
         [alc/up-dropdown-menu-trigger {:as-child true}
          [:button {:type "button"
                    :class-name "flex items-center space-x-2 rounded-lg transition-colors hover:bg-accent"}
           [user-profile-picture (merge @user- {:profile-size profile-size})]]]
         [alc/up-dropdown-menu-content {:class-name "w-56"
                                        :align "end"
                                        :force-mount true}
          [alc/up-dropdown-menu-label {:class-name "font-normal"}
           [:div {:class-name "flex flex-col space-y-1"}
            [:p {:class-name "text-sm leading-none font-medium"} (or name "User")]
            [:p {:class-name "text-xs leading-none text-muted-foreground"} (or email "user@example.com")]]]
          [alc/up-dropdown-menu-separator]
          (when @manages-users?-
            [:<>
             [alc/up-dropdown-menu-item {:on-click #(re/dispatch [:router/go [:user-management {:section "users"}]])}
              (menu-item-leading [alc/up-icon {:icon-type :UserGroupsIcon
                                               :class-name "h-4 w-4"}])
              [:span "User Management"]]
             [alc/up-dropdown-menu-separator]])
          [alc/up-dropdown-menu-sub
           [alc/up-dropdown-menu-sub-trigger {:class-name "gap-2"}
            (menu-item-leading [alc/up-icon {:icon-type :LightModeIcon
                                             :class-name "h-4 w-4"}])
            [:span "Theme"]]
           [alc/up-dropdown-menu-sub-content
            [alc/up-dropdown-menu-item {:on-click #(set-theme nil)}
             [alc/up-icon {:icon-type :LightModeIcon
                           :class-name "mr-2 h-4 w-4"}]
             [:span "Light"]]
            [alc/up-dropdown-menu-item {:on-click #(set-theme :dark-mode)}
             [alc/up-icon {:icon-type :DarkModeIcon
                           :class-name "mr-2 h-4 w-4"}]
             [:span "Dark"]]
            [alc/up-dropdown-menu-item {:on-click #(set-theme {:system true :light nil :dark :dark-mode})}
             [alc/up-icon {:icon-type :SettingsIcon
                           :class-name "mr-2 h-4 w-4"}]
             [:span "System"]]]]
          (when @admin?-
            [alc/up-dropdown-menu-item {:on-select #(.preventDefault %)
                                        :on-click #(re/dispatch [:anonymous/toggle])}
             (menu-item-leading [alc/up-checkbox {:checked (boolean @anonymous?-)
                                                  :style {:pointer-events :none}}])
             [:span "Anonymous Mode"]])
          (when @admin?-
            [alc/up-dropdown-menu-item {:on-click #(re/dispatch [:products/open-google-cloud-projects])}
             (menu-item-leading [alc/material-icon {:icon-type :cloud
                                                    :style {:display :block
                                                            :font-size 16
                                                            :line-height 1}}])
             [:span "Google Cloud Projects"]])
          [alc/up-dropdown-menu-separator]
          [alc/up-dropdown-menu-item {:on-click #(re/dispatch [:auth0/logout])}
           (menu-item-leading [alc/up-icon {:icon-type :ExitIcon
                                            :class-name "h-4 w-4"}])
           [:span "Log Out"]]]]))))

(defn page-header [{:keys [style]} & contents]
  (into [:span {:style (merge {:color (style/alc-color "Text/primary_header")
                               :font-size 24
                               :font-weight 400
                               :line-height 1.33}
                              style)}]
        contents))

(defn section-header [{:keys [style]} & contents]
  (into [:span {:style (merge {:color (style/alc-color "Text/primary_header")
                               :font-size 20
                               :font-weight 400
                               :line-height 1.4}
                              style)}]
        contents))

(defn nav-item [_]
  (let [cur-route- (re/subscribe [:router/current-route-])
        cid- (re/subscribe [:concepts/current-concept-id-])
        lid- (re/subscribe [:locations/current-selected-location-id-])
        ct- (re/subscribe [:core/selected-crew-type-])
        tf- (re/subscribe [:core/selected-timeframe-])
        tf-date-range- (re/subscribe [:core/selected-timeframe-date-range-])
        cur-query-params- (re/subscribe [:router/current-query-params-])
        closed?- (re/subscribe [:core/sidebar-closed?-])
        mobile?- (re/subscribe [:core/mobile-layout?-])]
    (fn [{:keys [icon-type label route route-keys cid? lid? ct? tf? agent-id? query-params on-click sub?]}]
      (let [route-k (first route)
            route-path-params (when (> (count route) 1)
                                (second route))
            qp (select-keys @cur-query-params- (cond-> core.d/carried-query-param-keys
                                                       agent-id?
                                                       (conj :agent-id :agentId)))
            route-query-params (cond-> (if (= (count route) 3)
                                         (last route)
                                         {})

                                       (and cid? @cid-)
                                       (assoc :cid @cid-)

                                       (and lid? @lid-)
                                       (assoc :lid @lid-)

                                       (and ct? @ct-)
                                       (assoc :ct @ct-)

                                       (and tf? @tf-)
                                       (assoc :tf @tf-)

                                       (and tf? (some-> @tf-date-range- (not-empty)))
                                       (assoc :start (core.d/format-start-end-date (first @tf-date-range-))
                                              :end (core.d/format-start-end-date (second @tf-date-range-)))

                                       qp
                                       (merge qp)

                                       query-params
                                       (merge query-params))
            selected? (or (and route (= route-k (first @cur-route-)))
                          (and route-keys (contains? route-keys (first @cur-route-))))]
        [(if sub? alc/up-sidebar-menu-sub-item alc/up-sidebar-menu-item)
         [(if sub? alc/up-sidebar-menu-sub-button alc/up-sidebar-menu-button)
          {:as-child true
           :is-active selected?
           :tooltip label
           :class-name (if sub?
                         "data-[active=true]:bg-up-background-hover [&>svg]:size-4"
                         "h-9 group-data-[collapsible=icon]:!size-9 data-[active=true]:border data-[active=true]:border-up-border-divider data-[active=true]:bg-up-background-hover [&>svg]:size-5")
           :style (when-not selected?
                    {:cursor :pointer})}
          [:div {:on-click (or on-click
                               (fn [e]
                                 (re/dispatch [:reports/refresh-nav-report route-k])
                                 (re/dispatch [:router/go [route-k route-path-params route-query-params]])
                                 (util/stop-event e)))
                 :style (cond-> {:height (if sub? 28 36)}
                                (or sub? (not @closed?-) @mobile?-)
                                (assoc :padding-left 12)

                                (not selected?)
                                (assoc :border "1px solid transparent"))}
           (when-not sub?
             [alc/material-icon {:icon-type icon-type
                                 :style {:color "var(--sidebar-foreground)"
                                         :font-size 20}}])
           [:span {:class-name "group-data-[collapsible=icon]:hidden"
                   :style {:color "var(--sidebar-foreground)"
                           :font-size 14}}
            label]]]]))))

(defn nav-group [{:keys [label]}]
  (let [expanded?- (re/subscribe [:core/nav-group-expanded?- label])
        closed?- (re/subscribe [:core/sidebar-closed?-])
        mobile?- (re/subscribe [:core/mobile-layout?-])
        cur-route- (re/subscribe [:router/current-route-])
        flyout?* (re/ratom false)
        close-timer* (re/ratom nil)
        mouse? #(= (.-pointerType %) "mouse")
        open-flyout! (fn [e]
                       (when (mouse? e)
                         (when-let [t @close-timer*]
                           (js/clearTimeout t)
                           (reset! close-timer* nil))
                         (reset! flyout?* true)))
        close-flyout! (fn [e]
                        (when (mouse? e)
                          (reset! close-timer* (js/setTimeout #(reset! flyout?* false) 150))))]
    (fn [{:keys [label icon-type items]}]
      (let [expanded? @expanded?-
            cur-route-name (first @cur-route-)
            child-selected? (boolean
                              (some (fn [{:keys [route route-keys]}]
                                      (or (and route (= (first route) cur-route-name))
                                          (and route-keys (contains? route-keys cur-route-name))))
                                    items))]
        (if (and @closed?- (not @mobile?-))
          ;; Collapsed: hovering (mouse) or tapping (touch) the icon flies the children out in a side menu.
          [alc/up-sidebar-menu-item
           [alc/up-dropdown-menu {:open @flyout?*
                                  :on-open-change #(reset! flyout?* %)
                                  :modal false}
            [alc/up-dropdown-menu-trigger {:as-child true}
             [:div {:on-pointer-enter open-flyout!
                    :on-pointer-leave close-flyout!
                    :class-name (str "flex items-center justify-center size-9 rounded-md cursor-pointer hover:bg-up-background-hover"
                                     (when child-selected?
                                       " border border-up-border-divider bg-up-background-hover"))}
              (when icon-type
                [alc/material-icon {:icon-type icon-type
                                    :style {:color "var(--sidebar-foreground)"
                                            :font-size 20}}])]]
            [alc/up-dropdown-menu-content {:side "right"
                                           :align "start"
                                           :style {:min-width 200}
                                           :on-pointer-enter open-flyout!
                                           :on-pointer-leave close-flyout!
                                           :on-click-capture #(reset! flyout?* false)}
             [alc/up-dropdown-menu-label label]
             (into [alc/up-sidebar-menu-sub {:class-name "mx-0 border-0 px-0"}]
                   (map (fn [args]
                          ^{:key (:id args)}
                          [nav-item (assoc args :sub? true)])
                        items))]]]
          ;; Expanded: inline collapsible group.
          [alc/up-sidebar-menu-item
           [alc/up-sidebar-menu-button {:class-name "h-9 [&>svg]:size-5"
                                        :style {:cursor :pointer
                                                :padding-left 12
                                                :margin-bottom 5}
                                        :on-click #(re/dispatch [:core/toggle-nav-group label])}
            (when icon-type
              [alc/material-icon {:icon-type icon-type
                                  :style {:color "var(--sidebar-foreground)"
                                          :font-size 20}}])
            [:span {:style (cond-> {:color "var(--sidebar-foreground)"
                                    :font-size 14}
                                   child-selected?
                                   (assoc :font-weight 600))}
             label]
            [alc/material-icon {:icon-type :expand-more
                                :class-name "ml-auto transition-transform duration-200"
                                :style (cond-> {:color "var(--sidebar-foreground)"
                                                :font-size 18}
                                               (not expanded?)
                                               (assoc :transform "rotate(-90deg)"))}]]
           (when expanded?
             (into [alc/up-sidebar-menu-sub {:style {:border-left :none
                                                     :padding-left 13}}]
                   (map (fn [args] ^{:key (:id args)}
                          [nav-item (assoc args :sub? true)])
                        items)))])))))

(defn with-nav [{:keys [header-container-style]} & contents]
  (fn [{:keys [header-container-style]} & contents]
    (into [:div {:style (merge {:flex "1 0 0px"
                                :display :flex
                                :overflow :hidden}
                               header-container-style)}]
          contents)))

(defn ->env-link
  [k]
  (let [env-links {:agent-builder {config/env-prod "https://agents.soundhound.com"
                                   config/env-staging "https://agents-stg.soundhound.com"}
                   :speech {config/env-prod "https://speech.soundhound.com"
                            config/env-staging "https://speech-stage.soundhound.com"}
                   :operator-portal {config/env-local "/"
                                     config/env-prod "/"}}
        by-env (get env-links k)]
    (or (get by-env config/ENV)
        (get by-env config/env-staging)
        (get by-env config/env-prod))))

(defn app-header-block [& [opts]]
  [alc/up-waffle-menu (merge
                        {:menu-sections [{:items [{:title "Agent Builder"
                                                   :description "Build and configure AI agents"
                                                   :url (->env-link :agent-builder)
                                                   :icon-src "/images/products/agent-builder.svg"
                                                   :icon-style {:inset "-3.75% -3.44% 8.75% 8.44%"
                                                                :width :auto
                                                                :height :auto}}
                                                  {:title "Speech Platform"
                                                   :description "Create and manage voice models"
                                                   :url (->env-link :speech)
                                                   :icon-src "/images/products/speech-platform.svg"}
                                                  {:title "Operations Portal"
                                                   :description "Monitor performance and configure deployments"
                                                   :url (->env-link :operator-portal)
                                                   :icon-src "/images/products/operator-portal.svg"}]}]
                         :active-item (some-> js/window .-location .-pathname)
                         :is-active-item (fn [item-url current]
                                           (boolean (and current item-url (s/starts-with? current item-url))))}
                        opts)])

(defn mobile-sidebar-close-button []
  (let [set-open-mobile (.-setOpenMobile (alc/use-sidebar))]
    [:button {:type "button"
              :aria-label "Close menu"
              :on-click #(set-open-mobile false)
              :style {:display :flex
                      :align-items :center
                      :justify-content :center
                      :border-radius 6
                      :cursor :pointer}}
     [alc/up-icon {:icon-type :CrossIcon
                   :style {:width 18
                           :height 18
                           :color "var(--text-secondary)"}}]]))

(defn mobile-sidebar-header []
  (let [theme- (re/subscribe [:core/theme-])]
    (fn []
      [:<>
       [:div {:style {:display :flex
                      :align-items :center
                      :justify-content :space-between
                      :flex-shrink 0
                      :padding "16px 4px 8px"}}
        [:img {:src (if (= @theme- :dark-mode)
                      "/images/oasys-logo-dark.svg"
                      "/images/oasys-logo.svg")
               :alt "SoundHound AI OASYS"
               :style {:height 36
                       :width :auto}}]
        [:f> mobile-sidebar-close-button]]
       [app-header-block
        {:align-offset -11
         :side-offset -4
         :trigger (fn [open?]
                    [:button {:type "button"
                              :aria-label "Switch product"
                              :style {:display :flex
                                      :align-items :center
                                      :align-self :flex-start
                                      :flex-shrink 0
                                      :height 60
                                      :cursor :pointer}}
                     [:img {:src "/images/products/operator-portal.svg"
                            :alt ""
                            :style {:width 20
                                    :height 20
                                    :margin-right 12
                                    :flex-shrink 0}}]
                     [:span {:style {:color "var(--sidebar-foreground)"
                                     :font-size 18
                                     :line-height "28px"
                                     :white-space :nowrap}}
                      "Operations Portal"]
                     [:span {:style {:display :flex
                                     :padding 8
                                     :border-radius 6
                                     :transform (when-not open? "rotate(-90deg)")
                                     :background "var(--buttons-ghost_normal)"}}
                      [alc/up-icon {:icon-type :SmallChevroneIcon
                                    :style {:width 20
                                            :height 20
                                            :color "var(--text-primary)"}}]]])}]
       [:div {:style {:flex-shrink 0
                      :height 1
                      :margin "8px 0"
                      :background "var(--border-divider)"}}]])))

(defn app-header-sidebar []
  [alc/up-sidebar-trigger])

(defn core-header []
  (let [mobile?- (re/subscribe [:core/mobile-layout?-])
        theme- (re/subscribe [:core/theme-])]
    [:div {:style {:flex (str "0 0 " header-height "px")
                   :display :flex
                   :align-items :center
                   :gap 8
                   :width "100%"
                   :padding "0 16px 0 14px"
                   :z-index (+ (style/z :medium) 2)}}
     [app-header-block]
     [:img {:src (if (= @theme- :dark-mode)
                   "/images/oasys-logo-dark.svg"
                   "/images/oasys-logo.svg")
            :style {:height 34
                    :width :auto
                    :margin "0 4px 0 8px"}}]
     [:div {:style {:flex 1
                    :display :flex
                    :align-items :center
                    :gap 8
                    :height "100%"}}
      [:div {:style {:flex 1}}]
      [:div {:style {:padding-left (if @mobile?- 4 8)}}
       [account-dropdown-v2 {:profile-size 36}]]]]))

(defn with-loading-header []
  [:div {:style {:flex 1
                 :display :flex
                 :flex-direction :column}}
   [core-header]])

(defn content-padding [mobile?]
  (if mobile? "0 8px 8px" "0 20px 20px 0"))

(defn select-concept-message []
  [empty-state/no-data-card {:text "No Concept Selected"
                             :material-icon-type :domain}])

(defn build-nav-options []
  [{:icon-type :insights
    :label "Insights"
    :product "voice-insights"
    :children [{:icon-type :dashboard
                :id :home
                :label "Dashboard"
                :route [:vi]
                :route-keys #{:vi}
                :cid? true :lid? true :ct? true :tf? true}
               {:icon-type :micc
                :id :sessions
                :label "Sessions"
                :route [:vi/sessions]
                :cid? true :lid? true :ct? true :tf? true}
               (when @(re/subscribe [:voice-insights/menu-items-feature-enabled?-])
                 {:icon-type :fastfood
                  :id :items
                  :label "Items"
                  :route [:vi/items]
                  :cid? true :lid? true :ct? true :tf? true})
               (when @(re/subscribe [:voice-insights/ces-feature-enabled?-])
                 {:icon-type :filter-7
                  :id :ces
                  :label "CES"
                  :route [:vi/ces]
                  :cid? true :lid? true :ct? true :tf? true})
               {:icon-type :schedule
                :id :duration
                :label "Duration"
                :route [:vi/duration]
                :cid? true :lid? true :ct? true :tf? true}
               (when @(re/subscribe [:voice-insights/upsell-feature-enabled?-])
                 {:icon-type :request-quote
                  :id :upsell
                  :label "Upsell"
                  :route [:vi/upsell]
                  :cid? true :lid? true :ct? true :tf? true})
               {:icon-type :model-training
                :id :ai-analyst
                :label "AI Analyst"
                :route [:vi/ai-analyst]
                :query-params {:ct "ai"}
                :cid? true :lid? true :ct? true :tf? true}
               ;; Role check first — derefing the agent list triggers its fetch,
               ;; which users without agent access can't make.
               (when (and (account.d/has-roles-access? @(re/subscribe [:account/user-roles-])
                                                       (:roles (get routes/routes :vi/agent)))
                          @(re/subscribe [:ai-agent/summary-agent- :vi/agent]))
                 {:icon-type :help-outline
                  :id :vi-agent
                  :label "Data Questions"
                  :route [:vi/agent]
                  :route-keys #{:vi/agent :vi/agent-session}
                  :cid? true :lid? true})]}
   {:id :voice-commerce
    :icon-type :shopping-cart
    :product "voice-commerce"
    :label "Voice Commerce"
    :children [{:icon-type :dashboard
                :id :voice-commerce-dashboard
                :label "Dashboard"
                :route [:vc]
                :cid? true :lid? true :tf? true}
               {:icon-type :receipt
                :id :voice-commerce-orders
                :label "Orders"
                :route [:vc/orders]
                :cid? true :lid? true :tf? true}
               (when (and (account.d/has-roles-access? @(re/subscribe [:account/user-roles-])
                                                       (:roles (get routes/routes :vc/agent)))
                          @(re/subscribe [:ai-agent/summary-agent- :vc/agent]))
                 {:icon-type :help-outline
                  :id :vc-agent
                  :label "Data Questions"
                  :route [:vc/agent]
                  :route-keys #{:vc/agent :vc/agent-session}
                  :cid? true :lid? true})]}
   {:id :smart-ordering
    :icon-type :storefront
    :product "smart-ordering"
    :label "Smart Ordering"
    :children [{:icon-type :micc
                :id :so-sessions
                :label "Sessions"
                :route [:so/sessions]
                :cid? true :lid? true :tf? true}
               (when (account.d/has-roles-access? @(re/subscribe [:account/user-roles-])
                                                  (:roles (get routes/routes :so/asr-training)))
                 {:icon-type :model-training
                  :id :so-asr-training
                  :label "ASR Training"
                  :route [:so/asr-training]
                  :cid? true :lid? true :tf? true})
               (when (and (account.d/has-roles-access? @(re/subscribe [:account/user-roles-])
                                                       (:roles (get routes/routes :so/agent)))
                          @(re/subscribe [:ai-agent/summary-agent- :so/agent]))
                 {:icon-type :help-outline
                  :id :so-agent
                  :label "Data Questions"
                  :route [:so/agent]
                  :route-keys #{:so/agent :so/agent-session}
                  :cid? true :lid? true})]}
   {:id :oasys
    :icon-type :hub
    :product "oasys"
    :label "OASYS"
    :children [{:icon-type :micc
                :id :oasys-sessions
                :label "Sessions"
                :route [:oasys/sessions]
                :cid? true :tf? true}
               (when (and (account.d/has-roles-access? @(re/subscribe [:account/user-roles-])
                                                       (:roles (get routes/routes :oasys/agent)))
                          @(re/subscribe [:ai-agent/summary-agent- :oasys/agent]))
                 {:icon-type :help-outline
                  :id :oasys-agent
                  :label "Data Questions"
                  :route [:oasys/agent]
                  :route-keys #{:oasys/agent :oasys/agent-session}
                  :cid? true})]}
   {:icon-type :assessment
    :id :reports
    :label "Reports"
    :children [(when (and (account.d/has-roles-access? @(re/subscribe [:account/user-roles-])
                                                       (:roles (get routes/routes :reports/list)))
                          (true? @(re/subscribe [:reports/has-non-nav?-])))
                 {:icon-type :list-alt
                  :id :reports-list
                  :label "Your Reports"
                  :route [:reports/list]
                  :route-keys #{:reports/list :reports/individual :reports/individual-session :reports/individual-edit}
                  :cid? true :lid? true :ct? true :tf? true})
               ;; Role check first — derefing the agent list triggers its fetch,
               ;; which users without agent access can't make. The builder authors
               ;; against a product, so it stays hidden until the tenant's products
               ;; have loaded and there is at least one.
               (when (and (account.d/has-roles-access? @(re/subscribe [:account/user-roles-])
                                                       (:roles (get routes/routes :reports/new)))
                          (not-empty (util/when-not-loading @(re/subscribe [:products/list-])))
                          @(re/subscribe [:ai-agent/report-builder-agent-]))
                 {:icon-type :add
                  :id :reports-new
                  :label "New Report"
                  :route [:reports/new]
                  :route-keys #{:reports/new :reports/new-session}
                  :on-click #(re/dispatch [:ai-agent/start-new-report])})]}
   (when-let [namespace (some-> @(re/subscribe [:system-configuration/namespaces-])
                                (util/when-not-loading)
                                (first))]
     {:icon-type :tune
      :id :configuration
      :label "Configuration"
      :route [:configuration {:namespace namespace}]})])

(defn nav-parent-label [route-name]
  (some (fn [{:keys [label children]}]
          (when (some (fn [{:keys [route route-keys]}]
                        (or (= route-name (first route))
                            (contains? route-keys route-name)))
                      children)
            label))
        (build-nav-options)))

(defn build-nav-items [{:keys [roles nav-list product-ids injected-nav-items]}]
  (let [nav-list-configuration-by-route (when (and nav-list (not (util/loading? nav-list)))
                                          (->> nav-list
                                               (mapv :navigation-configuration)
                                               (util/group-by-distinct :route)))
        visible? (fn [{:keys [route injected?]}]
                   (or injected?
                       (when route
                         (let [route-config (get routes/routes (first route))]
                           (or (not (:roles route-config))
                               (account.d/has-roles-access? roles (:roles route-config)))))))
        apply-override (fn [{:keys [route] :as nav-config}]
                         (let [route-path (some-> route (first) (router/route-details->href nil nil))
                               matching-nav-list-route (get nav-list-configuration-by-route route-path)]
                           (if matching-nav-list-route
                             (let [{:keys [icon-type label]} matching-nav-list-route]
                               (cond-> nav-config
                                       icon-type (assoc :icon-type (util/->kebab-case-keyword icon-type))
                                       (not-empty label) (assoc :label label)))
                             nav-config)))
        items (->> (concat (mapv (fn [i] (assoc i :injected? true))
                                 (or injected-nav-items []))
                           (when nav-list-configuration-by-route
                             (filter #(or (not (:product %))
                                          (contains? product-ids (:product %)))
                                     (build-nav-options))))
                   (keep (fn [entry]
                           (cond
                             (nil? entry)
                             nil

                             (:children entry)
                             (let [children (->> (:children entry)
                                                 (keep (fn [child]
                                                         (when (and child (visible? child))
                                                           (apply-override child))))
                                                 (vec))]
                               (when (not-empty children)
                                 (assoc entry :children children)))

                             (visible? entry)
                             (apply-override entry))))
                   (vec))
        known-route-uris (into #{} (map :uri (vals routes/routes)))
        nav-config->item (fn [{:keys [route label sort-order]}]
                           (let [route-name (router/path->route-name route)]
                             {:id route
                              :label label
                              :sort-order sort-order
                              :route [route-name]
                              :route-keys #{route-name}
                              :cid? true :lid? true :ct? true :tf? true}))
        parent-key (fn [{:keys [id label]}]
                     (some-> (or id label) (name)))
        {standalone-configs false grouped-configs true}
        (->> (vals nav-list-configuration-by-route)
             (filter (fn [{:keys [route icon-type label parent]}]
                       (and route label
                            (or icon-type (parent-key parent))
                            (not (contains? known-route-uris route)))))
             (group-by #(-> % :parent parent-key some?)))
        sort-nav-entries (fn [entries]
                           (sort-by (juxt #(let [sort-order (:sort-order %)]
                                             (if (number? sort-order) sort-order ##Inf))
                                          #(some-> % :label (s/lower-case)))
                                    entries))
        new-nav-groups (->> grouped-configs
                            (group-by #(-> % :parent parent-key))
                            (mapv (fn [[k configs]]
                                    (let [{:keys [label icon-type product sort-order]} (->> configs
                                                                                            (map :parent)
                                                                                            (apply merge-with #(or %1 %2)))]
                                      {:id k
                                       :label (or label (util/->capitalize-words k))
                                       :icon-type (or (some-> icon-type (util/->kebab-case-keyword))
                                                      :analytics)
                                       :product product
                                       :sort-order sort-order
                                       :children (->> configs
                                                      (map nav-config->item)
                                                      (sort-nav-entries)
                                                      (vec))}))))
        same-group? (fn [item {:keys [id label]}]
                      (and (:children item)
                           (or (= (some-> (:id item) (name)) id)
                               (= (:label item) label))))
        {merged-groups true injected-groups false} (group-by (fn [group]
                                                               (boolean (some #(same-group? % group) items)))
                                                             new-nav-groups)
        flex-lone-navigation-children (fn [items]
                                        (let [lone-navigation-ids (->> items
                                                                       (mapcat :children)
                                                                       (keep :navigation-id)
                                                                       (frequencies)
                                                                       (keep (fn [[id n]] (when (= n 1) id)))
                                                                       (set))
                                              lone? #(contains? lone-navigation-ids (:navigation-id %))
                                              flexed (->> items
                                                          (mapcat (fn [{:keys [children icon-type product]}]
                                                                    (->> children
                                                                         (filter lone?)
                                                                         (map #(-> %
                                                                                   (update :icon-type (fn [i] (or i icon-type)))
                                                                                   (assoc :product product)))))))
                                              remaining (keep (fn [{:keys [children] :as item}]
                                                                (if (not-empty children)
                                                                  (some->> children
                                                                           (remove lone?)
                                                                           (not-empty)
                                                                           (vec)
                                                                           (assoc item :children))
                                                                  item))
                                                              items)
                                              [before-reports from-reports] (split-with #(not= (:id %) :reports) remaining)]
                                          (vec (concat before-reports flexed from-reports))))]
    (-> (reduce (fn [items {:keys [children] :as group}]
                  (let [idx (->> items
                                 (keep-indexed (fn [idx item]
                                                 (when (same-group? item group) idx)))
                                 (first))]
                    (update-in items [idx :children] into children)))
                items
                merged-groups)
        (flex-lone-navigation-children)
        (into (->> standalone-configs
                   (map (fn [{:keys [icon-type] :as config}]
                          (assoc (nav-config->item config)
                            :icon-type (util/->kebab-case-keyword icon-type))))
                   (concat injected-groups)
                   (sort-nav-entries))))))

(defn with-nav-core [_ _]
  (let [roles- (re/subscribe [:account/user-roles-])
        nav-list- (re/subscribe [:reports/nav-list-])
        mobile?- (re/subscribe [:core/mobile-layout?-])
        closed?- (re/subscribe [:core/sidebar-closed?-])
        cid- (re/subscribe [:concepts/current-concept-id-])
        cur-route- (re/subscribe [:router/current-route-])
        save-scroll! (fn [e] (reset! sidebar-scroll-top* (.-scrollTop (.-target e))))
        restore-scroll! (fn [el] (when el (set! (.-scrollTop el) @sidebar-scroll-top*)))
        product-ids- (re/subscribe [:products/reference-ids-])]
    (fn [{:keys [header-container-style cid injected-nav-items header-filters?]} & contents]
      (let [items (build-nav-items {:roles @roles-
                                    :nav-list @nav-list-
                                    :product-ids @product-ids-
                                    :injected-nav-items injected-nav-items})
            cur-route-name (first @cur-route-)
            route-matches? (fn [{:keys [route route-keys]}]
                             (or (and route (= (first route) cur-route-name))
                                 (and route-keys (contains? route-keys cur-route-name))))
            active-parent (some (fn [{:keys [children] :as entry}]
                                  (when (or (route-matches? entry)
                                            (some route-matches? children))
                                    entry))
                                items)
            collapsed? (and @closed?- (not @mobile?-))]
        (re/dispatch [:products/select (:product active-parent)])
        [:div {:class-name "min-h-0"
               :style (merge {:flex "1 0 0px"
                              :display :flex
                              :position :relative
                              :overflow :clip}
                             header-container-style)}
         [alc/up-sidebar {:collapsible "icon"
                          :style {:height "100%"}}
          [alc/up-sidebar-content
           [alc/up-sidebar-group {:class-name "px-3 py-2"
                                  :style {:padding-top 0
                                          :flex "1 1 0%"
                                          :min-height 0
                                          :display :flex
                                          :flex-direction :column}}
            (if @mobile?-
              [mobile-sidebar-header]
              [:<>
               [:div {:style {:display :flex
                              :align-items :center
                              :justify-content (if collapsed? :center :flex-start)
                              :flex-shrink 0
                              :height (if collapsed? 36 52)
                              :gap 12
                              :padding (if collapsed? 0 "0 12px")}}
                [:img {:src "/images/products/operator-portal.svg"
                       :alt "Operations Portal"
                       :style {:width 20
                               :height 20
                               :flex-shrink 0}}]
                (when-not collapsed?
                  [:span {:style {:color "var(--sidebar-foreground)"
                                  :font-size 18
                                  :line-height "28px"
                                  :white-space :nowrap}}
                   "Operations Portal"])]
               [:div {:style {:flex-shrink 0
                              :height 1
                              :margin "8px 0"
                              :background "var(--border-divider)"}}]])
            [:div {:ref restore-scroll!
                   :on-scroll save-scroll!
                   :style {:flex "1 1 0%"
                           :min-height 0
                           :overflow-y :auto}}
             (into [alc/up-sidebar-menu {:style {:gap 6
                                                 :flex-shrink 0}}]
                   (map (fn [{:keys [children label icon-type] :as item}]
                          (if (not-empty children)
                            ^{:key (or (:id item) label)}
                            [nav-group {:label label
                                        :icon-type icon-type
                                        :items children}]
                            ^{:key (:id item)}
                            [nav-item item]))
                        items))]]]]
         (into [:div {:class-name "flex flex-1 flex-col overflow-hidden"}]
               (if (or (false? header-filters?) (and @cid- cid))
                 contents
                 [[:div {:style {:padding (content-padding @mobile?-)
                                 :min-height "100%"}}
                   [alc/up-panel-layout {:class-name "core-panel-layout-373"}
                    [alc/up-panel-left {:full true}
                     [select-concept-message]]]]]))]))))

(defn with-header [_ _]
  (let [mobile?- (re/subscribe [:core/mobile-layout?-])
        nav-expanded?- (re/subscribe [:core/nav-id-expanded?- layout-nav-id])
        org-name- (re/subscribe [:auth0/ns-organization-display-name-])
        org-metadata- (re/subscribe [:auth0/ns-organization-metadata-])
        concepts- (re/subscribe [:concepts/list-data-])
        cid- (re/subscribe [:concepts/current-concept-id-])
        set-ids (fn [this]
                  (let [{:keys [cid]} (re/props this)]
                    (when (and cid (not= (str @cid-) cid)) (re/dispatch [:concepts/select cid]))))]
    (re/create-class
      {:component-did-mount
       set-ids

       :component-did-update
       set-ids

       :reagent-render
       (fn [{:keys [nav-container-style concept-dropdown?]
             :or {concept-dropdown? false}}
            & contents]
         (let [nav-width (if @mobile?- mobile-nav-width base-nav-width)
               concepts (util/when-not-loading @concepts-)]
           (into [:div {:style (merge {:flex 1
                                       :display :flex
                                       :flex-direction :column}
                                      nav-container-style)}
                  [:div {:style {:flex (str "0 0 " (if @mobile?- mobile-header-height header-height) "px")
                                 :display :flex
                                 :align-items :center
                                 :width "100%"
                                 :z-index (inc (style/z :medium))}}
                   [:div {:style {:background-color (style/color :primary :base)
                                  :cursor :pointer
                                  :flex (str "0 0 " nav-width "px")
                                  :display :flex
                                  :align-items :center
                                  :padding (if @mobile?- "0 10px" "0 20px")
                                  :height "100%"}
                          :on-click (if @mobile?-
                                      #(re/dispatch [:core/toggle-nav layout-nav-id])
                                      #(re/dispatch [:router/go [:home]]))}
                    [:img {:src "/images/logo-light.svg"
                           :height (if @mobile?- 20 30)
                           :width :auto}]
                    (when @mobile?-
                      [c/material-icon {:icon-type (if @nav-expanded?- :west :east)
                                        :style {:color (style/color :white :base)
                                                :margin-left 5}}])]
                   [:div {:style {:border-bottom (str "1px solid " (style/color :gray :shade-7))
                                  :flex 1
                                  :display :flex
                                  :align-items :center
                                  :padding (if @mobile?- "0 5px 0 10px" "0 20px 0 40px")
                                  :height "100%"}}
                    (if (and concept-dropdown? (> (count concepts) 1))
                      [concepts/concepts-dropdown {:on-change (fn [_ d]
                                                                (let [v (aget d "value")]
                                                                  (re/dispatch [:router/go-with-merged-query-params
                                                                                {:cid v}])))}]
                      (when (or @org-name- (:logo @org-metadata-))
                        (let [org-logo? (:logo @org-metadata-)
                              org-name? (and @org-name- (or (not (:logo @org-metadata-))
                                                            (:force-show-name-and-logo @org-metadata-)))]
                          [:div {:style {:display :flex
                                         :align-items :center}}
                           (when org-logo?
                             [:img {:src (anon.d/logo-url (:logo @org-metadata-))
                                    :height 20
                                    :style {:margin-right (if org-name? 10 0)
                                            :height 20}}])
                           (when org-name?
                             [:span {:style {:color (style/color :primary :base)
                                             :font-size 18
                                             :font-weight style/font-bold-weight}}
                              (anon.d/org-label @org-name-)])])))
                    [:div {:style {:flex 1}}]
                    [:div {:style {:padding-left (if @mobile?- 4 8)}}
                     [account-dropdown]]]]]
                 contents)))})))

(defn header-divider []
  [:div {:style {:width 1
                 :height 30
                 :background-color (style/alc-color "Border/divider")}}])

(defn header-filters-sheet [{:keys [open?* active-count]} & fields]
  [:<>
   [alc/up-button {:variant "ghost"
                   :icon-type :FilterIcon
                   :on-click #(reset! open?* true)
                   :style {:padding-right 10}}
    [:span "Filters"]
    (when (pos? active-count)
      [:span {:style {:background-color (style/alc-color "Text/primary_header")
                      :border-radius 10
                      :color (style/alc-color "Background/card_hex")
                      :font-size 11
                      :font-weight 600
                      :line-height "18px"
                      :min-width 18
                      :padding "0 5px"
                      :text-align :center}}
       active-count])]
   [alc/up-sheet {:open @open?*
                  :on-open-change #(reset! open?* %)}
    [alc/up-sheet-content {:side "bottom"
                           :style {:border-radius "16px 16px 0 0"
                                   :padding-bottom "calc(16px + env(safe-area-inset-bottom))"}}
     [alc/up-sheet-header
      [alc/up-sheet-title "Filters"]
      [alc/up-sheet-description {:class-name "sr-only"}
       "Filter the data shown on this page"]]
     (into [:div {:style {:display :flex
                          :flex-direction :column
                          :gap 16
                          :padding "0 16px"}}]
           (keep (fn [{:keys [label boxed? cmp]}]
                   (when cmp
                     [:div {:style {:display :flex
                                    :flex-direction :column
                                    :gap 6}}
                      [:span {:style {:color (style/alc-color "Text/secondary")
                                      :font-size 13
                                      :font-weight 500}}
                       label]
                      [:div {:style (cond-> {:display :flex
                                             :align-items :center
                                             :min-height 44}
                                            boxed?
                                            (assoc :border (str "1px solid " (style/alc-color "Border/divider"))
                                                   :border-radius 8
                                                   :padding "0 4px"))}
                       cmp]]))
                 fields))
     [:div {:style {:padding "8px 16px 0"}}
      [alc/up-button {:on-click #(reset! open?* false)
                      :style {:min-height 44
                              :width "100%"}}
       "Done"]]]]])

(defn with-header-core [_ _]
  (let [mobile?- (re/subscribe [:core/mobile-layout?-])
        cur-route- (re/subscribe [:router/current-route-])
        concepts- (re/subscribe [:concepts/list-data-])
        cid- (re/subscribe [:concepts/current-concept-id-])
        lid- (re/subscribe [:locations/current-selected-location-id-])
        user-concept-ids- (re/subscribe [:account/user-concept-ids-])
        locations- (re/subscribe [:locations/by-current-concept-id-])
        roles- (re/subscribe [:account/user-roles-])
        nav-list- (re/subscribe [:reports/nav-list-])
        product-ids- (re/subscribe [:products/reference-ids-])
        theme- (re/subscribe [:core/theme-])
        vw- (re/subscribe [:core/viewport-width])
        lg?- (re/subscribe [:core/breakpoint-min?- :lg])
        ai-user?- (re/subscribe [:account/has-every-role?- [:ai-user]])
        ct- (re/subscribe [:core/selected-crew-type-])
        tf- (re/subscribe [:core/selected-timeframe-])
        tf-date-range- (re/subscribe [:core/selected-timeframe-date-range-])
        agents- (re/subscribe [:core/ai-agent-list-])
        qps- (re/subscribe [:router/current-query-params-])
        filters-open?* (re/ratom false)
        full-render?* (re/ratom false)
        sel-concept- (re/subscribe [:concepts/selected-])
        stored-filters- (re/subscribe [:core/stored-header-filters-])
        set-ids (fn [this]
                  (let [{:keys [cid lid header-filters?]} (re/props this)
                        stored @stored-filters-
                        user-concept? (fn [id] (some #(= (str id) (str %)) @user-concept-ids-))
                        url-filters (core.d/->header-filters @qps-)]
                    (cond
                      ;; A page with its own selections leaves the URL and stored filters alone, but the nav still
                      ;; needs a concept to load against
                      (false? header-filters?)
                      (when-not @cid-
                        (when-let [cid (cond
                                         (user-concept? (:cid stored)) (:cid stored)
                                         (= (count @user-concept-ids-) 1) (first @user-concept-ids-))]
                          (re/dispatch [:concepts/select cid])))

                      (and (s/blank? cid) (user-concept? (:cid stored)))
                      (re/dispatch [:router/go-with-merged-query-params (merge (zipmap core.d/header-filter-keys (repeat nil))
                                                                               stored)])

                      :else
                      (do
                        (when (and (user-concept? cid) (not= stored url-filters))
                          (re/dispatch [:core/store-header-filters url-filters]))
                        (when (and cid (not= cid "") (not= (str @cid-) cid))
                          (if (user-concept? cid)
                            (re/dispatch [:concepts/select cid])
                            (re/dispatch [:concepts/select nil])))
                        (when (and (not cid) (= (count @user-concept-ids-) 1))
                          (re/dispatch [:concepts/select (first @user-concept-ids-)]))
                        (when (not= (str @lid-) (str lid))
                          (re/dispatch [:locations/select lid]))
                        (when (and (some-> @locations- (util/when-not-loading) (count) (= 1))
                                   (not @lid-)
                                   (not lid)
                                   (= (str @cid-) (str cid)))
                          (re/dispatch [:router/go-with-merged-query-params {:lid (:id (first @locations-))}]))
                        (when (and (or (not cid) (= cid "") (not (user-concept? cid)))
                                   @cid-
                                   (user-concept? @cid-))
                          (re/dispatch [:router/go-with-merged-query-params {:cid (str @cid-)}]))))))
        set-theme #(when-let [el (.querySelector js/document "html")]
                     (let [class-list (.-classList el)]
                       (if (= @theme- :dark-mode)
                         (.add class-list "dark")
                         (.remove class-list "dark")))
                     (reset! full-render?* true))
        product- (re/subscribe [:products/selected-])]
    (re/create-class
      {:component-did-mount
       #(do
          (set-theme)
          (re/dispatch [:core/init-drawer-listener])
          (set-ids %)
          (re/dispatch [:products/sync-google-cloud-projects]))

       :component-did-update
       #(do
          (set-theme)
          (set-ids %)
          (re/dispatch [:products/sync-google-cloud-projects]))

       :reagent-render
       (fn [{:keys [nav-container-style concept-dropdown? location-dropdown? concept-dropdown-config
                    location-dropdown-config header-contents crew-type-radio? timeframe-dropdown? agent-dropdown?
                    injected-nav-items]
             :or {concept-dropdown? true
                  location-dropdown? true
                  crew-type-radio? true
                  timeframe-dropdown? true
                  agent-dropdown? false}}
            & contents]
         @theme-
         (let [selected-location- (when @lid-
                                    (re/subscribe [:locations/by-current-location-id-]))
               co-brand-image-url (when (and selected-location- (not (util/loading? @selected-location-)))
                                    (some-> @selected-location- :metadata :co-brand-image-url (anon.d/logo-url)))
               concepts (util/when-not-loading @concepts-)
               concept-dropdown? (and concept-dropdown? (> (count concepts) 1))
               location-dropdown? (and location-dropdown? (some-> (util/when-not-loading @locations-)
                                                                  (count)
                                                                  (> 1)))
               crew-type-radio? (and crew-type-radio? @ai-user?-)
               mobile? @mobile?-
               compact? (not @lg?-)
               route-name (first @cur-route-)
               route-config (get routes/routes route-name)
               [parent-label cur-route-label] (if route-config
                                                [(nav-parent-label route-name) (:label route-config)]
                                                (some (fn [{:keys [label children route]}]
                                                        (if children
                                                          (some #(when (= route-name (-> % :route (first)))
                                                                   [label (:label %)])
                                                                children)
                                                          (when (= route-name (first route))
                                                            [nil label])))
                                                      (build-nav-items {:roles @roles-
                                                                        :nav-list @nav-list-
                                                                        :product-ids @product-ids-
                                                                        :injected-nav-items injected-nav-items})))
               show-breadcrumbs? (and (> @vw- 1300) (not-empty cur-route-label))
               breadcrumb-label (fn [label]
                                  [:span {:style {:color (style/alc-color "Text/secondary")
                                                  :font-size 14
                                                  :font-weight 400
                                                  :line-height 1.43}}
                                   label])
               breadcrumb-chevron [alc/up-icon {:icon-type :ChevronRightIcon
                                                :style {:color (style/alc-color "Border/secondary_inp")}}]
               concept-dropdown-cmp [concepts/concepts-dropdown (merge {:image-url (when @cid-
                                                                                     (-> (or (some-> @sel-concept- :brand-configuration :logo-url)
                                                                                             (concepts.d/concept-logo-url @cid-))
                                                                                         (anon.d/logo-url)))
                                                                        :on-change (fn [v]
                                                                                     (let [route-k (first @cur-route-)]
                                                                                       (if (= route-k :not-found)
                                                                                         (do
                                                                                           (util/add-query-params {:cid v})
                                                                                           (re/dispatch [:concepts/select v]))
                                                                                         (do
                                                                                           (re/dispatch [:locations/clear-selected])
                                                                                           (re/dispatch [:router/go
                                                                                                         [route-k
                                                                                                          (second @cur-route-)
                                                                                                          (merge (some-> @cur-route-
                                                                                                                         (last)
                                                                                                                         (select-keys (concat [:ct :tf :start :end] core.d/carried-query-param-keys)))
                                                                                                                 (when-not (s/blank? v)
                                                                                                                   {:cid (util/parse-int v)}))]])))))}
                                                                       concept-dropdown-config)]
               location-dropdown-cmp [locations/locations-dropdown (merge {:placeholder "All Locations"
                                                                           :clearable? true
                                                                           :fixed-icon-type :LocationIcon
                                                                           :on-change (fn [v]
                                                                                        (re/dispatch [:router/go-with-merged-query-params
                                                                                                      {:lid v}]))}
                                                                          location-dropdown-config)]
               timeframe-dropdown-cmp (fn [style]
                                        [core.views/timeframe-dropdown
                                         (cond-> {:style style}
                                                 (= @product- "voice-insights")
                                                 (assoc :show-live? @(re/subscribe [:voice-insights/live-data?-])
                                                        :show-most-recent? true))])
               filter-summary (when compact?
                                (->> [(when concept-dropdown?
                                        (some-> @sel-concept-
                                                (util/when-not-loading)
                                                (anon.d/concept-label)))
                                      (when location-dropdown?
                                        (some-> selected-location- (deref) (util/when-not-loading) :label (anon.d/location-label)))
                                      (when crew-type-radio?
                                        (if (= @ct- "ai") "AI Agent" "Human Agent"))
                                      (when timeframe-dropdown?
                                        (core.views/timeframe-label @tf- @tf-date-range-))
                                      (when agent-dropdown?
                                        (let [agent-id (-> @qps- (util/->kebab-case-keywords) :agent-id)]
                                          (some #(when (= (:public-id %) agent-id) (:label %))
                                                (util/when-not-loading @agents-))))]
                                     (remove s/blank?)
                                     (not-empty)))
               account-cmp [:div {:style {:padding-left (if mobile? 4 8)}}
                            [account-dropdown-v2 {:profile-size (if mobile? 32 36)}]]]
           (into [:div {:style (merge {:flex 1
                                       :display :flex
                                       :flex-direction :column
                                       :min-width 0}
                                      nav-container-style)}
                  [google-cloud-projects/google-cloud-projects-banner {:padding (if mobile? "6px 8px" "6px 16px 6px 14px")}]
                  [:div {:style {:flex (str "0 0 " (if mobile? mobile-header-height header-height) "px")
                                 :display :flex
                                 :align-items :center
                                 :gap 8
                                 :width "100%"
                                 :padding (if mobile? "0 8px 0 12px" "0 16px 0 14px")
                                 :z-index (+ (style/z :medium) 2)}}
                   (if mobile?
                     [app-header-sidebar]
                     [:<>
                      [app-header-block]
                      [:img {:src (if (= @theme- :dark-mode)
                                    "/images/oasys-logo-dark.svg"
                                    "/images/oasys-logo.svg")
                             :style {:height 34
                                     :width :auto
                                     :margin "0 4px 0 8px"}}]])
                   (when-not mobile?
                     [app-header-sidebar])
                   (if compact?
                     [:<>
                      [:div {:style {:flex 1}}]
                      header-contents
                      (when (or concept-dropdown? location-dropdown? crew-type-radio? timeframe-dropdown? agent-dropdown?)
                        [header-filters-sheet {:open?* filters-open?*
                                               :active-count (count (filter true? [(and concept-dropdown? (some? @cid-))
                                                                                   (and location-dropdown? (some? @lid-))
                                                                                   (and crew-type-radio? true)
                                                                                   (and timeframe-dropdown? true)]))}
                         (when concept-dropdown?
                           {:label "Concept"
                            :boxed? true
                            :cmp concept-dropdown-cmp})
                         (when location-dropdown?
                           {:label "Location"
                            :boxed? true
                            :cmp location-dropdown-cmp})
                         (when crew-type-radio?
                           {:label "Agent Type"
                            :cmp [core.views/crew-type-radio]})
                         (when timeframe-dropdown?
                           {:label "Timeframe"
                            :cmp (timeframe-dropdown-cmp {:flex "1 1 auto"})})
                         (when agent-dropdown?
                           {:label "Agent"
                            :cmp [core.views/agent-dropdown {:style {:flex "1 1 auto"}}]})])
                      account-cmp]
                     [:<>
                      (when (or concept-dropdown? location-dropdown? co-brand-image-url)
                        [header-divider])
                      (when co-brand-image-url
                        [:img {:src co-brand-image-url
                               :style {:height :auto
                                       :margin-right 24
                                       :width 40}}])
                      [:div {:style {:flex 1
                                     :display :flex
                                     :align-items :center
                                     :gap 8
                                     :height "100%"}}
                       (when concept-dropdown?
                         concept-dropdown-cmp)
                       (when (and concept-dropdown? location-dropdown?)
                         [alc/up-icon {:icon-type :ChevronRightIcon
                                       :style {:color (style/alc-color "Border/secondary_inp")}}])
                       (when location-dropdown?
                         location-dropdown-cmp)
                       (when show-breadcrumbs?
                         [:<>
                          (when (or concept-dropdown? location-dropdown?)
                            breadcrumb-chevron)
                          (when (and parent-label (not= parent-label cur-route-label))
                            [:<>
                             [breadcrumb-label parent-label]
                             breadcrumb-chevron])
                          [breadcrumb-label cur-route-label]])
                       [:div {:style {:flex 1}}]
                       header-contents
                       (when crew-type-radio?
                         [core.views/crew-type-radio])
                       (when timeframe-dropdown?
                         (timeframe-dropdown-cmp nil))
                       (when agent-dropdown?
                         [core.views/agent-dropdown])
                       account-cmp]])]
                  (when filter-summary
                    [:button {:type "button"
                              :on-click #(reset! filters-open?* true)
                              :style {:background :none
                                      :border :none
                                      :color (style/alc-color "Text/secondary")
                                      :cursor :pointer
                                      :font-size 12
                                      :line-height "16px"
                                      :margin-top -4
                                      :overflow :hidden
                                      :padding (if mobile? "0 8px 8px 12px" "0 16px 8px 12px")
                                      :text-align :right
                                      :text-overflow :ellipsis
                                      :white-space :nowrap}}
                     (s/join " · " filter-summary)])]
                 contents)))})))

(defn logged-in-with-header-nav []
  (let [scroll-container-id "with-header-nav-scroll-panel"
        mobile?- (re/subscribe [:core/mobile-layout?-])
        cur-route- (re/subscribe [:router/current-route-])
        on-scroll-set?* (re/ratom false)
        set-on-scroll (fn [_]
                        (let [perform-set-on-scroll
                              #(do
                                 (re/dispatch [:core/set-layout-content-container-scroll-position
                                               (.-scrollTop %)])
                                 (when (or (not @on-scroll-set?*) (not (some-> % (.-onscroll))))
                                   (set! (.-onscroll %)
                                         (fn [e]
                                           (re/dispatch [:core/set-layout-content-container-scroll-position
                                                         (.. e -target -scrollTop)])))
                                   (reset! on-scroll-set?* false)))]
                          (if-let [scroll-container (.getElementById js/document scroll-container-id)]
                            (perform-set-on-scroll scroll-container)
                            (util/wait 500 #(some-> (.getElementById js/document scroll-container-id)
                                                    (perform-set-on-scroll))))))]
    (re/create-class
      {:component-did-mount set-on-scroll
       :component-did-update set-on-scroll
       :reagent-render
       (fn [{:keys [scroll-panel-style
                    scroll-parent-panel-style
                    content-container-style
                    page-header-title
                    page-header-contents
                    left-contents
                    data-loading?
                    go-back-nav?
                    go-back-label
                    go-back-nav-route]
             :as args}
            & contents]
         (let [cur-route-config (some->> @cur-route- (first) (get routes/routes))
               page-header-title (or page-header-title (:label cur-route-config))]
           [main args
            [with-header args
             [with-nav {}
              [:div {:style (merge {:display :flex
                                    :flex "1 1 auto"}
                                   scroll-parent-panel-style)}
               (when left-contents
                 (into [:div {:style {:flex "0 0 auto"
                                      :display :flex}}]
                       left-contents))
               [:div {:id scroll-container-id
                      :style (merge {:flex "1 1 auto"
                                     :display :flex
                                     :flex-direction :column
                                     :overflow :auto
                                     :height "100%"}
                                    scroll-panel-style)}
                (into [:div {:style (merge {:flex "0 0 auto"
                                            :padding (if @mobile?- "20px 20px 30px" "40px 40px 60px")
                                            :width "100%"}
                                           content-container-style)}
                       (when (or page-header-title page-header-contents go-back-nav?)
                         (into [:div {:style {:display :flex
                                              :align-items :center
                                              :margin-bottom (if @mobile?- 15 20)}}
                                (when go-back-nav?
                                  [c/white-on-background-button {:style {:padding "0px 12px 0 8px"}
                                                                 :on-click #(re/dispatch [:router/go (or go-back-nav-route
                                                                                                         [:home])])}
                                   [:div {:style {:color :inherit
                                                  :display :flex
                                                  :align-items :center}}
                                    [c/material-icon {:icon-type :arrow-back
                                                      :style {:color :inherit
                                                              :font-size 16}}]
                                    [:span {:style {:color :inherit
                                                    :margin-left 3}}
                                     (or go-back-label "Back")]]])
                                (when (and go-back-nav? page-header-title)
                                  [:div {:style {:background-color (style/color :primary :base)
                                                 :margin "0 20px"
                                                 :height 30
                                                 :width 1}}])
                                (when page-header-title
                                  (into [c/h2 {:style {:color (style/color :on-background :base)}}]
                                        [page-header-title]))]
                               page-header-contents))]
                      (if data-loading?
                        [[c/spinner-block {:spinner-args {:color (style/color :primary :base)
                                                          :size 30}
                                           :style {:padding 60}}]]
                        contents))]]]]]))})))

(re/reg-event-db
  :layout/show-panel-right re/middleware
  (fn [db [panel-right]]
    (assoc-in db (conj root-path :active-panel-right) panel-right)))

(re/reg-event-db
  :layout/hide-panel-right re/middleware
  (fn [db]
    (-> db
        (assoc-in (conj root-path :active-panel-right) nil)
        (assoc-in (conj root-path :panel-right-expanded?) false))))

(re/reg-event-db
  :layout/toggle-panel-right-expanded re/middleware
  (fn [db]
    (update-in db (conj root-path :panel-right-expanded?) not)))

(re/reg-event-db
  :layout/expand-panel-right re/middleware
  (fn [db]
    (assoc-in db (conj root-path :panel-right-expanded?) true)))

(re/reg-sub
  :layout/panel-right-expanded?-
  (fn [db]
    (get-in db (conj root-path :panel-right-expanded?))))

(defn active-panel-right [db]
  (get-in db (conj root-path :active-panel-right)))

(re/reg-sub :layout/active-panel-right- active-panel-right)

;; Below md the panel layout hides the main panel whenever a right panel is open, so
;; only one of the two shows at a time (the md cutoff is set by the CSS overrides above).
(re/reg-sub
  :layout/single-pane?-
  :<- [:core/breakpoint-min?- :md]
  (fn [md?]
    (not md?)))

(defmulti
  panel-right-view
  identity)

(defn active-panel-right-cmp []
  (let [active-panel-right- (re/subscribe [:layout/active-panel-right-])
        expanded?- (re/subscribe [:layout/panel-right-expanded?-])]
    (when @active-panel-right-
      (let [[panel-id args] @active-panel-right-]
        ^{:key (pr-str args)}
        [alc/up-panel-right {:class-name (when @expanded?- "panel-right-expanded")}
         [panel-right-view panel-id args]]))))

(defn standard-content-container [{:keys [id container-style style title-style title description-style description
                                          header-contents sticky-header?]} & contents]
  [:div {:id id
         :style (merge {:display :flex
                        :flex-direction :column
                        :width "100%"
                        :min-height "100%"}
                       container-style)}
   (when title
     [:div {:style (cond-> {:background-color (style/alc-color "Background/card_hex")
                            :display :flex
                            :flex-direction :column
                            :gap 8
                            :padding "12px 16px 16px"}
                           sticky-header?
                           (assoc :position :sticky
                                  :top 0
                                  :left 0
                                  :z-index (inc (style/z :medium)))
                           title-style
                           (merge title-style))}
      (into [:span {:style {:color (style/alc-color "Text/primary_header")
                            :font-size 24
                            :font-weight 400
                            :line-height 1.33}}
             title])
      (when description
        [:span {:style (merge {:color (style/alc-color "Text/secondary")
                               :font-size 18
                               :font-weight 400
                               :line-height 1.33}
                              description-style)}
         description])
      header-contents])
   (into [:div {:style (merge {:flex 1
                               :display :flex
                               :flex-direction :column
                               :padding (if title
                                          "0 16px 12px"
                                          "12px 16px")}
                              style)}]
         contents)])

(defn close-mobile-sidebar-on-route-change [{:keys [route]}]
  (let [set-open-mobile (.-setOpenMobile (alc/use-sidebar))]
    (react/useEffect (fn []
                       (set-open-mobile false)
                       js/undefined)
                     #js [route])
    nil))

(defn sidebar-provider [& _]
  (let [closed?- (re/subscribe [:core/sidebar-closed?-])
        cur-route- (re/subscribe [:router/current-route-])]
    (fn [& contents]
      (into [alc/up-sidebar-provider {:class-name "min-h-0"
                                      :style {"--sidebar-width" "15rem"
                                              "--sidebar-width-icon" "3.75rem"
                                              :flex "1 0 0px"
                                              :min-width 0}
                                      :open (not @closed?-)
                                      :on-open-change #(re/dispatch [:core/set-sidebar-closed (not %)])}
             [:f> close-mobile-sidebar-on-route-change {:route (pr-str @cur-route-)}]]
            contents))))

(defn logged-in-with-header-nav-core [_]
  (let [on-scroll-set?* (re/ratom false)
        vi-config- (re/subscribe [:voice-insights/concept-configuration-])
        roles- (re/subscribe [:account/user-roles-])
        tags- (re/subscribe [:vi-tags/list-by-current-concept-id-])
        set-on-scroll (fn [_]
                        (let [perform-set-on-scroll
                              #(do
                                 (re/dispatch [:core/set-layout-content-container-scroll-position
                                               (.-scrollTop %)])
                                 (when (or (not @on-scroll-set?*) (not (some-> % (.-onscroll))))
                                   (set! (.-onscroll %)
                                         (fn [e]
                                           (re/dispatch [:core/set-layout-content-container-scroll-position
                                                         (.. e -target -scrollTop)])))
                                   (reset! on-scroll-set?* false)))]
                          (if-let [scroll-container (.getElementById js/document core-scroll-content-id)]
                            (perform-set-on-scroll scroll-container)
                            (util/wait 500 #(some-> (.getElementById js/document core-scroll-content-id)
                                                    (perform-set-on-scroll))))))
        active-panel-right- (re/subscribe [:layout/active-panel-right-])
        mobile?- (re/subscribe [:core/mobile-layout?-])
        panel-right-expanded?- (re/subscribe [:layout/panel-right-expanded?-])]
    (re/create-class
      {:component-did-mount set-on-scroll
       :component-did-update set-on-scroll
       :reagent-render (fn [{:keys [scroll-id scroll-container-style] :as args} & contents]
                         (when (account.d/has-some-role? @roles- [:voice-insights-user])
                           @vi-config- @tags-)
                         [sidebar-provider
                          [with-header-core args
                           [with-nav-core args
                            [alc/active-dialog]
                            [active-toasts]
                            [:div {:style {:padding (content-padding @mobile?-)
                                           :min-height "100%"}}
                             [alc/up-panel-layout {:class-name "core-panel-layout-373"}
                              [alc/up-panel-left {:full (if @active-panel-right- false true)
                                                  :class-name (when (and @active-panel-right- @panel-right-expanded?-)
                                                                "panel-left-collapsed")}
                               (into [:div {:id (or scroll-id core-scroll-content-id)
                                            :style (merge {:flex 1
                                                           :width "100%"
                                                           :overflow :auto}
                                                          scroll-container-style)}]
                                     contents)]
                              ;; Breaks PanelLeft's `+ [data-slot=panel-right]` match, which
                              ;; hides it below xl — the CSS overrides up top move that to md.
                              [:div {:style {:display :none}}]
                              [active-panel-right-cmp]]]]]])})))

(defn logged-in-with-header-nav-vi [_]
  (re/create-class
    {:component-did-mount
     #(re/dispatch [:voice-insights/initialize-filters])
     :reagent-render
     (fn [args & contents]
       (into [logged-in-with-header-nav-core (merge {}      ;; defaults
                                                    args)]
             contents))}))

(defn logged-in-with-header-nav-smart-ordering [_]
  (re/create-class
    {:component-did-mount
     #(re/dispatch [:core/initialize-filters])
     :reagent-render
     (fn [args & contents]
       (into [logged-in-with-header-nav-core (merge {:crew-type-radio? false} ;; defaults
                                                    args)]
             contents))}))

(defn logged-in-with-header-nav-oasys [_]
  (re/create-class
    {:component-did-mount
     #(re/dispatch [:core/initialize-filters])
     :reagent-render
     (fn [args & contents]
       (into [logged-in-with-header-nav-core (merge {:crew-type-radio? false} ;; defaults
                                                    args)]
             contents))}))

(defn logged-in-with-header-nav-voice-commerce [_]
  (re/create-class
    {:component-did-mount
     #(re/dispatch [:core/initialize-filters])
     :reagent-render
     (fn [args & contents]
       (into [logged-in-with-header-nav-core (merge {:crew-type-radio? false} ;; defaults
                                                    args)]
             contents))}))
